<script lang="ts" setup>
import type { FormSubmitEvent } from "@nuxt/ui";
import type { FormState } from "~~/models/forms";
import type { IOurRecaptchaComposition } from "~/composables/useVueRecaptcha";
import { z } from "zod";
import { useVueRecaptcha } from "~/composables/useVueRecaptcha";

const { locale, t } = useI18n();
const localePath = useLocalePath();
const { returnUrl } = useRoute().query;
const { user, fetch: fetchUserSession } = useUserSession();

const { data: cmsData } = await useFetch("/api/cms/account-page", { query: { locale: locale } });

const state = reactive({
  email: undefined,
  password: undefined,
});

const registrationLabel = computed(() => {
  const link = cmsData.value?.loginLinkRegistration;

  if (!link) {
    return "";
  }

  const linkUrl = localePath(`/${link.slug}`);
  return `${cmsData.value?.loginActionRegistration} <a href="${linkUrl}" class="hover:text-primary underline">${link.title}</a>`;
});

const formValidationEmail = t("form.validation.email");
const formValidationRequired = t("form.validation.required");
const formValidationMinLengthPassword = t("form.validation.min-length", { MinLength: "8" });

const schema = z.object({
  email: z.email({ error: issue => issue.input === undefined ? formValidationRequired : formValidationEmail }),
  password: z
    .string({ error: issue => issue.input === undefined ? formValidationRequired : "Invalid password." })
    .min(8, formValidationMinLengthPassword),
});

const form = ref(); // remove?
const formState = ref<FormState>("mounting");
const errorMessage = ref("");

let recaptcha: IOurRecaptchaComposition | undefined;

async function onSubmit(event: FormSubmitEvent<z.output<typeof schema>>) {
  formState.value = "pending";
  errorMessage.value = "";

  try {
    const recaptchaToken = recaptcha && await recaptcha.execute("login") || "";
    await $fetch("/api/ecom/auth", {
      method: "POST",
      body: event.data,
      query: { locale: locale.value },
      headers: { "X-Recaptcha-Token": recaptchaToken },
    });

    formState.value = "success";

    // technically we don't need to do full page navigation, but it's easier for state
    await fetchUserSession();
    if (user.value?.isMultiCustomer) {
      await navigateTo("/auth/select-customer", { replace: true, external: true });
      return;
    }
    await navigateTo(returnUrl?.toString() || "/", { replace: true, external: true });
  }
  catch (error) {
    formState.value = "error";
    errorMessage.value = createFetchErrorMessage(error, {
      defaultMessage: cmsData.value?.loginMessageInvalidCredentials,
      unauthorizedMessage: cmsData.value?.loginMessageInvalidCredentials,
    });
  }
}

onMounted(async () => {
  recaptcha = await useVueRecaptcha();
  recaptcha?.show();

  formState.value = "idle";
});

onBeforeUnmount(() => {
  recaptcha?.hide();
});

useHead({
  title: () => cmsData.value?.loginTitle || null,
});
</script>

<template>
  <div>
    <PageTitle :data="cmsData?.loginTitle" class="mb-md" />

    <div v-if="formState === 'success'" class="flex h-28 place-content-center items-center">
      <Loading class="size-8" />
    </div>

    <FeedbackMessage v-if="errorMessage" :msg="errorMessage" type="fail" class="mb-md" />

    <UForm
      v-if="formState !== 'success'"
      ref="form" :schema="schema" :state="state"
      class="gap-lg flex flex-col"
      aria-live="assertive" role="form" @submit="onSubmit"
    >
      <div class="gap-md flex flex-col">
        <UFormField :label="cmsData?.loginLabelUsername" name="email" required>
          <UInput
            v-model="state.email" size="xl" required type="email" autocomplete="email"
            inputmode="email" spellcheck="false"
          />
        </UFormField>

        <div>
          <UFormField :label="cmsData?.loginLabelPassword" name="password" required>
            <UInput v-model="state.password" size="xl" required type="password" autocomplete="current-password" />
          </UFormField>

          <div class="flex justify-end">
            <NuxtLinkLocale to="/auth/forgot-password" class="hover:text-primary text-sm underline">
              {{ cmsData?.loginActionForgotPassword }}
            </NuxtLinkLocale>
          </div>
        </div>
      </div>

      <div class="gap-sm lg:gap-md flex flex-wrap items-center">
        <UButton
          type="submit" :loading="formState === 'pending'" :disabled="formState === 'mounting'"
          variant="solid" color="primary" size="xl"
        >
          {{ cmsData?.loginActionSubmit }}
        </UButton>

        <div class="text-sm" v-html="registrationLabel" />
      </div>
    </UForm>
  </div>
</template>

<style scoped>

</style>
